<script lang="ts">
	import { applicationFormId } from "@components/applicationFormId.js";
	import Link from "@components/common/Link.svelte";
	import Media from "@components/common/Media.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import { getTranslate } from "@utils/translate.js";
	import { onMount } from "svelte";
	import type { ClassValue } from "svelte/elements";
	import eventIcon from "@core/assets/icons/event.svg?raw";
	import locationIcon from "@core/assets/icons/location.svg?raw";
	import discDogClublogo from "@core/assets/logo.svg";
	import type { Competition } from "@core/schema/Competition.js";
	import { getApplicationPath } from "@core/schema/paths/getApplicationPath.js";
	import { getApplicationsPath } from "@core/schema/paths/getApplicationsPath.js";
	import { toDateMonthFormat } from "@core/utils/toDateMonthFormat.js";
	import { toDateMonthYearFormat } from "@core/utils/toDateMonthYearFormat.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./Banner.translations.json" with { type: "json" };
	import Countdown from "./Countdown.svelte";
	import Icon from "./common/Icon.svelte";

	const { class: customClass, competition }: { class?: ClassValue; competition: Competition } = $props();
	const translate = getTranslate(translations);
	const { tenant } = getGlobalTemplateProps();
	let now = $state(new Date().toISOString());
	onMount(() => {
		const interval = setInterval(() => {
			now = new Date().toISOString();
		}, 1000);
		return () => {
			clearInterval(interval);
		};
	});
</script>

<section class={["mb-8 lg:mb-16 z-20", customClass]}>
	<div class="flex flex-col md:grid md:grid-cols-2 md:items-center gap-4 md:gap-8 lg:gap-20 xl:gap-32">
		<div class="max-md:order-2">
			<h1 class="max-xl:text-lg max-w-xl xl:max-w-1xl">{competition.headline[tenant.locale]}</h1>

			<div class="text-base lg:leading-10 flex flex-col xl:flex-row gap-2 lg:gap-3 mb-4 lg:mb-8">
				<span
					class="bg-secondary-50 shadow-sm rounded-lg w-fit px-4 py-2 xl:py-3 flex items-center gap-1 font-semibold leading-normal"
				>
					<Icon icon={locationIcon} />
					{competition.location[tenant.locale]}
				</span>
				<span
					class="bg-secondary-50 shadow-sm rounded-lg w-fit px-4 py-2 xl:py-3 flex items-center gap-1 font-semibold leading-normal"
				>
					<Icon icon={eventIcon} />

					{toDateMonthFormat(competition.competitionStart, tenant.locale)} – {toDateMonthYearFormat(
						competition.competitionEnd,
						tenant.locale,
					)}
				</span>
			</div>

			{#if competition.registrationStart > now}
				<div class="flex flex-col gap-3">
					<p class="text-base font-bold">{translate("toStartOfRegistration")}</p>
					<Countdown target={competition.registrationStart} />
				</div>
			{:else if competition.registrationEnd > now}
				<div class="flex flex-col gap-3">
					<p class="text-base font-bold">{translate("toEndOfRegistration")}</p>
					<Countdown target={competition.registrationEnd} />
				</div>
			{:else if competition.competitionStart > now}
				<div class="flex flex-col gap-3">
					<p class="text-base font-bold">{translate("toStartOfCompetition")}</p>
					<Countdown target={competition.competitionStart} />
				</div>
			{/if}

			<div class="flex flex-col lg:flex-row gap-2 mt-8">
				{#if now >= competition.registrationStart}
					{#if now < competition.registrationEnd}
						<Link
							class="max-md:w-full max-w-96"
							variant={ButtonVariant.Primary}
							href="{getApplicationPath(competition, tenant.locale)}#{applicationFormId[tenant.locale]}"
						>
							{translate("registrationButton")}
						</Link>
					{/if}
					<Link
						class="max-md:w-full max-w-96"
						variant={ButtonVariant.PrimaryOutline}
						href={getApplicationsPath(competition, tenant.locale)}
					>
						{translate("listOfApplications")}
					</Link>
				{/if}
			</div>
		</div>
		<Media
			class="max-md:w-40"
			source={{ src: discDogClublogo, type: "image/svg+xml" }}
			width={474}
			height={422}
			alt="DiscDog Club logo"
		/>
	</div>
</section>
